import axios, { AxiosError } from 'axios';
import {
    SigninPayloadModel,
    AgentRegisterAPIPayload,
    UpdateUserRegisterAPI,
    InActiveAgentAPIPayload,
    GraphDetailsPayload,
    CreateNewResourcePayload,
    CreateNewUserProfilePayload,
    CreateClientFacingUrlPayload,
    SendClientFacingUrlPayload,
    ExtendUrlExpiryPayload,
    DeleteClientFacingUrlPayload,
    RothConversionCalcPayload
} from '../Models/axiosModel';

axios.defaults.withCredentials = true;
axios.defaults.baseURL = process.env.REACT_APP_API_CALL_URL;

let apiBuffer: Array<{ name: string, controller: AbortController }> = [];

/** Backend sends this when the session/cookie is invalid. */
const UNAUTHENTICATED_STATUS_CODE = -999;

/** Paths where an unauthenticated response should not force a login redirect. */
const PUBLIC_PATHS = new Set([
    '/',
    '/login',
    '/register',
    '/registration-success',
    '/privacy-policy',
    '/terms-and-conditions',
    '/user-agreement',
    '/disclaimer',
    '/resetpassword',
    '/verify_user',
    '/profile/calculators',
    '/contact-us',
    '/rothQuoter',
    '/password-setup',
]);

let isRedirectingToLogin = false;

const getErrorPayload = (payload: any) =>
    payload?.error ?? payload?.data?.error ?? null;

const isUnauthenticatedError = (payload: any): boolean => {
    const error = getErrorPayload(payload);
    if (!error) return false;
    if (error.statusCode === UNAUTHENTICATED_STATUS_CODE) return true;
    const message = typeof error.message === 'string' ? error.message.toLowerCase() : '';
    return message.includes('invalid authentication');
};

/**
 * Redirects to /login on unauthenticated API responses so callers do not
 * surface a snackbar and leave the user stuck on a protected page.
 */
const redirectToLoginIfUnauthenticated = (payload: any): boolean => {
    if (!isUnauthenticatedError(payload)) return false;
    if (isRedirectingToLogin) return true;

    const path = globalThis.location?.pathname ?? '';
    if (PUBLIC_PATHS.has(path)) return true;

    isRedirectingToLogin = true;
    globalThis.location.assign('/login');
    return true;
};

const responseBody = (response: any) => {
    if (response?.Title === undefined) {
        const data = response.data;
        redirectToLoginIfUnauthenticated(data);
        return data;
    }
}

const handleAxiosError = (err: AxiosError) => {
    // @ts-ignore
    if (err?.response !== null && err?.response !== undefined) {
        console.log("response.response.data.error--", err?.response?.data)
        redirectToLoginIfUnauthenticated(err.response.data);
    }
    return err;
}

// Catch auth failures for any axios call (including ones outside `requests`).
axios.interceptors.response.use(
    (response) => {
        redirectToLoginIfUnauthenticated(response?.data);
        return response;
    },
    (error) => {
        redirectToLoginIfUnauthenticated(error?.response?.data);
        return Promise.reject(error);
    }
);

const requests = {
    get: async (url: string) => {
        try {


            // @ts-ignore
            const controller = new AbortController();
            // @ts-ignore
            apiBuffer.push({ name: url, controller: controller })
            const response = await axios.get(url,
                {
                    headers: {
                        "Content-Type": "application/json"
                    },
                    signal: controller.signal
                });
            // @ts-ignore
            if (response?.flagStatus === false && response?.error !== "" && response?.error !== null && response?.error !== undefined) {
                alert("Something went wrong.!");
            }
            else {
                return responseBody(response);
            }
        } catch (err: any) {
            return handleAxiosError(err);
        }
    },
    fileUploadPost: async (url: string, body: any) => {
        try {
            const response = await axios.post(url, body, {
                headers: {
                    "Content-Type": "multipart/form-data"
                }
            });
            return responseBody(response);
        } catch (err: any) {
            return handleAxiosError(err);
        }
    },
    post: async (url: string, body: any, onAbort?: () => void) => {
        try {
            // @ts-ignore
            const controller = new AbortController();
            // @ts-ignore
            apiBuffer.push({ name: url, controller: controller })
            // @ts-ignore
            const response = await axios.post(url, body, {
                headers: {
                    "Content-Type": "application/json"
                },
                signal: controller.signal
            });
            return responseBody(response);
        } catch (err: any) {
            if (axios.isCancel(err)) {
                console.log("Request was canceled", err);
                // @ts-ignore
                apiBuffer = apiBuffer.filter((api: any) => api.name !== url)
                if (onAbort) console.log("onabort called");
            } else {
                console.log("not aborted", err)
            }
            return handleAxiosError(err);
        }
    },
    put: async (url: string, body: any) => {
        try {
            const response = await axios.put(url, body, {
                headers: {
                    "Content-Type": "application/json"
                }
            });
            return responseBody(response);
        } catch (err: any) {
            return handleAxiosError(err);
        }
    },
    delete: async (url: string) => {
        try {
            const response = await axios.delete(url, {
                headers: {
                    "Content-Type": "application/json"
                }
            });
            return responseBody(response);
        } catch (err: any) {
            return handleAxiosError(err);
        }
    }
};

const GetCompanyLogoAPI = {
    get: () => requests.get('api/global/get-company-logo')
}

const UserMeGlobalAPI = {
    get: () => requests.get("api/global/me")
}

const signinAPI = {
    post: (payload: SigninPayloadModel) => requests.post("api/auth/login", payload)
}

const getUserDetailsAPI = {
    get: (
        agent_id?: number | null,
        date_filter_flag?: boolean | null,
        from_timestamp?: string | null,
        to_timestamp?: string | null
    ) => {

        const params = new URLSearchParams();

        // Always pass agent_id and date_filter_flag
        params.append('agent_id', agent_id?.toString() || '0');
        params.append('date_filter_flag', date_filter_flag ? "true" : "false");

        // Optionally add date filters if provided
        if (from_timestamp && to_timestamp) {
            params.append('from_timestamp', from_timestamp);
            params.append('to_timestamp', to_timestamp);
        }

        return requests.get(`api/global/get-user-details?${params.toString()}`);
    }
}

const getAgentDetailsAPI = {
    get: () => requests.get("api/global/get-agent-details")
}

const getAgentStatusDetailsAPI = {
    get: () => requests.get("api/global/get-status-details")
}

const logoutAPI = {
    post: () => requests.post("api/auth/logout", {})
}

const signinWithGoogleAPI = {
    get: () => requests.get("api/auth/outbound_auth_url")
}

const removeAgentAPI = {
    delete: (agent_id: number) => requests.delete(`api/user/remove-agent?agent_id=${agent_id}`)
}

const agentRegisterAPI = {
    post: (payload: AgentRegisterAPIPayload) => requests.post(`api/user/registration`, payload)
}

const updateAgentRegisterAPI = {
    put: (payload: UpdateUserRegisterAPI) => requests.put(`api/user/update-agent-data`, payload)
}

const inActiveAgentAPI = {
    put: (payload: InActiveAgentAPIPayload) => requests.put('api/auth/inactive-agent', payload)
}

const getGraphDetails = {
    post: (payload: GraphDetailsPayload) => requests.post("api/global/get-graph-details", payload)
}

const getparticularUserProfileDetail = {
    get: (user_id: number) => requests.get(`api/user/get-user-profile-detail?user_profile_id=${user_id}`)
}

const getparticularUserProfileDetailForSharedProfile = {
    get: (userToken: string, userProfileId: string) => {
        const params = new URLSearchParams();
        params.append('token', userToken);
        params.append('user_profile_id', userProfileId);
        return requests.get(`api/user/get-user-profile-detail?${params.toString()}`);
        // requests.get(`api/user/get-user-profile-detail?user_profile_id=${user_id}`)
    }
}

const createNewUserProfile = {
    post: (payload: CreateNewUserProfilePayload) => requests.post("api/user/create-new-user-profile", payload)
}

const updateUserProfile = {
    put: (payload: any) => requests.put("api/user/update-user-profile", payload)
}

const deleteUserProfile = {
    delete: (user_profile_id: number) => requests.delete(`api/user/delete-user-profile?user_profile_id=${user_profile_id}`)
}

const getAdminDashboardDetailsAPI = {
    get: (startDate?: string | null, endDate?: string | null) => {
        const params = new URLSearchParams();

        // Add parameters only if they exist
        if (startDate) {
            params.append('startDate', startDate);
        }
        if (endDate) {
            params.append('endDate', endDate);
        }

        // Use the constructed params string in the API request URL
        // const url = `api/dashboard/dashboard${params.toString() ? '?' + params.toString() : ''}`;
        const url = `api/dashboard/v2/dashboard${params.toString() ? '?' + params.toString() : ''}`;


        // Make the request
        return requests.get(url);
    }
}

const getCompanyResourceData = {
    get: () => requests.get('api/resource/get-company-resource-data')
}

const createNewResourceAPI = {
    post: (payload: CreateNewResourcePayload) => requests.post("api/resource/create-new-resource", payload)
}

const uploadResourceAPI = {
    fileUploadPost: (payload: any) => requests.fileUploadPost("api/resource/upload-resource", payload)
}

const deleteResourceAPI = {
    delete: (resource_id: string) => requests.delete(`api/resource/delete-resource?resource_id=${resource_id}`)
}

const updateResourceAPI = {
    put: (payload: any) => requests.put("api/resource/update-resource", payload)
}

const getUploadResourceDataAPI = {
    get: (thumbnail_file_id: any) => requests.get(`api/resource/get-upload-resource-data?resource_id=${thumbnail_file_id}&image_flag=true`)
}

const updateAgentData = {
    put: (payload: any) => requests.put('api/user/update-agent-data', payload)
}

const getSuperAdminDashboardDetailsAPI = {
    get: (startDate?: string | null, endDate?: string | null) => {
        const params = new URLSearchParams();

        // Add parameters only if they exist
        if (startDate) {
            params.append('startDate', startDate);
        }
        if (endDate) {
            params.append('endDate', endDate);
        }

        // Use the constructed params string in the API request URL
        const url = `api/dashboard${params.toString() ? '?' + params.toString() : ''}`;

        // Make the request
        return requests.get(url);
    }
}

const sendVerificationCodeAPI = {
    get: () => requests.get("api/global/send-verification-code")
}

const changePasswordAPI = {
    put: (payload: any) => requests.put(`api/auth/reset-password?`, payload)
}

const forgetPasswordAPI = {
    post: (payload: any) => requests.post("api/auth/forget-password", payload)
}

const resetPasswordAPI = {
    post: (payload: any) => {
        // Extract email and token from payload for the query params
        const { email, token, ...passwordData } = payload;

        // Create the URL with query params for email and token
        const url = `api/auth/reset-password?email=${encodeURIComponent(email)}&verify_token=${encodeURIComponent(token)}`;

        // Send only password and confirm_password in the payload
        return requests.post(url, passwordData);
    }
};

const newUserRegisterAPI = {
    post: (payload: any) => requests.post("api/user/registration", payload)
}

const getUserRoleDetailsAPI = {
    get: () => requests.get("api/global/get-user-role-details")
}

const resendVerifyLinkAPI = {
    post: (payload: any) => requests.post("api/auth/resend-verification-link", payload)
}

const verifyTokenAPI = {
    get: (verify_token: any) => requests.get(`api/auth/verify-user?verify_token=${verify_token}`)
}

const assignAgentAPI = {
    put: (payload: any) => requests.put('/api/user/update-user-assigned-agent', payload)
}

const getUploadedFilesAPI = {
    get: () => requests.get("/api/extract-pdf/get-all-pdfs")
}

const uploadPdfForExtractionAPI = {
    fileUploadPost: (payload: any) => requests.fileUploadPost("/api/extract-pdf/upload-pdf", payload)
}

const getSelectedPdfImagesAPI = {
    get: (file_id: number) => requests.get(`/api/extract-pdf/get-images?file_id=${file_id}`)
}

const extractPdfDataAPI = {
    post: (payload: any) => requests.post("/api/extract-pdf/extract-pdf-data", payload)
}

const deletePdfFileAPI = {
    delete: (file_id: number) => requests.delete(`/api/extract-pdf/delete-pdf?file_id=${file_id}`)
}

const checkPdfExtractionStatusAPI = {
    get: (log_id: string) => requests.get(`/api/extract-pdf/check-pdf-extraction-status?log_id=${log_id}`)
}

const verifySSOtokenAPI = {
    get: (token: string) => requests.get(`api/sso/home?token=${token}`)
}

const getClientFacingUrlDataAPI = {
    get: (user_profile_id: string) => requests.get(`api/global/get-client-facing-url-data?user_profile_id=${user_profile_id}`)
}

const createClientFacingUrlAPI = {
    post: (payload: CreateClientFacingUrlPayload) => requests.post("api/auth/create-client-facing-url", payload)
}

const sendClientFacingUrlAPI = {
    post: (payload: SendClientFacingUrlPayload) => requests.post("api/global/send-email-url", payload)
}

const extendURLExpiryAPI = {
    put: (payload: ExtendUrlExpiryPayload) => requests.put("api/global/extend-url-expiry", payload)
}

const deleteClientFacingUrlAPI = {
    delete: (payload: DeleteClientFacingUrlPayload) => requests.delete(`api/global/delete-client-facing-url?public_token=${payload.public_token}&user_profile_id=${payload.user_profile_id}`)
}

const getHistoricalInflationRateAPI = {
    get: () => requests.get("api/global/get-historical-inflation-rate")
}

const getCpiCategoryChanges = {
    get: () => requests.get("api/global/cpi-category-changes")
}


const postContactUsAPI = {
    post: (payload: any) => requests.post("api/global/contact-us", payload)
}

const spendDownCalculatorAPI = {
    post: (payload: any) => requests.post("api/global/spend-down-calculator", payload)
}

const downloadProfilePdfAPI = {
    get: (user_profile_id: string) => requests.get(`api/user/download-profile-pdf?user_profile_id=${user_profile_id}`)
}

const rothConversionQuoterCalcAPI = {
    post: (payload: RothConversionCalcPayload) => requests.post("/api/global/roth-conversion-quoter-calculator", payload)
}

const newUserRegisterAPIV2 = {
    post: (payload: any) => requests.post("api/user/v2/registration", payload)
}

const getAgentListByFilterAPI = {
    get: (payload: any) => requests.get(`api/user/admin/agents?status=${payload.status}`)
}

const resendVerificationEmailAPI = {
    post: (payload: any) => requests.post("api/user/admin/resend-verification-email", payload)
}

const approveAgentAPI = {
    post: (payload: any) => requests.post("api/user/admin/approve-agent", payload)
}

const rejectAgentAPI = {
    post: (payload: any) => requests.post("api/user/admin/reject-agent", payload)
}

const getAgentDetailsWithTokenAPI = {
    get: (token: string) => requests.get(`api/auth/password-setup/validate?setup_token=${token}`)
}

const setNewPasswordAPI = {
    post: (payload: any) => requests.post("api/auth/password-setup/complete", payload)
}

const setSampleProfileAPI = {
    put: (payload: any) => requests.put("api/global/set-sample-profile-flag", payload)
}

const agent = {
    UserMeGlobalAPI,
    signinAPI,
    getUserDetailsAPI,
    getAgentDetailsAPI,
    getAgentStatusDetailsAPI,
    logoutAPI,
    signinWithGoogleAPI,
    removeAgentAPI,
    agentRegisterAPI,
    getparticularUserProfileDetail,
    updateAgentRegisterAPI,
    inActiveAgentAPI,
    getGraphDetails,
    getAdminDashboardDetailsAPI,
    getCompanyResourceData,
    createNewResourceAPI,
    uploadResourceAPI,
    deleteResourceAPI,
    updateResourceAPI,
    createNewUserProfile,
    updateUserProfile,
    deleteUserProfile,
    getSuperAdminDashboardDetailsAPI,
    getUploadResourceDataAPI,
    updateAgentData,
    sendVerificationCodeAPI,
    changePasswordAPI,
    forgetPasswordAPI,
    resetPasswordAPI,
    newUserRegisterAPI,
    getUserRoleDetailsAPI,
    resendVerifyLinkAPI,
    verifyTokenAPI,
    assignAgentAPI,
    GetCompanyLogoAPI,
    getUploadedFilesAPI,
    uploadPdfForExtractionAPI,
    getSelectedPdfImagesAPI,
    extractPdfDataAPI,
    deletePdfFileAPI,
    checkPdfExtractionStatusAPI,
    verifySSOtokenAPI,
    getClientFacingUrlDataAPI,
    createClientFacingUrlAPI,
    sendClientFacingUrlAPI,
    extendURLExpiryAPI,
    deleteClientFacingUrlAPI,
    getparticularUserProfileDetailForSharedProfile,
    getHistoricalInflationRateAPI,
    getCpiCategoryChanges,
    postContactUsAPI,
    spendDownCalculatorAPI,
    downloadProfilePdfAPI,
    rothConversionQuoterCalcAPI,
    newUserRegisterAPIV2,
    getAgentListByFilterAPI,
    resendVerificationEmailAPI,
    approveAgentAPI,
    rejectAgentAPI,
    getAgentDetailsWithTokenAPI,
    setNewPasswordAPI,
    setSampleProfileAPI

};

export default agent;